import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useApi } from '../../hooks/useApi';
import { useAuth } from '../../hooks/useAuth';
import LoadingSpinner from '../../components/Common/LoadingSpinner';
import DataTable from '../../components/Common/DataTable';
import { ArrowLeftIcon, PencilIcon, CalendarIcon } from '@heroicons/react/24/outline';

const VehicleDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { get, loading } = useApi();
  const { user: currentUser } = useAuth();
  const [vehicle, setVehicle] = useState(null);
  const [trips, setTrips] = useState([]);
  const [tripsPageInfo, setTripsPageInfo] = useState({
    totalElements: 0,
    totalPages: 0,
    currentPage: 0,
    size: 10
  });
  const [activeTab, setActiveTab] = useState('info');
  const [loadingTrips, setLoadingTrips] = useState(false);

  const isAdmin = currentUser?.roles?.includes('ADMIN') || false;

  useEffect(() => {
    fetchVehicle();
  }, [id]);

  useEffect(() => {
    if (location.state?.tab === 'trips') {
      setActiveTab('trips');
    }
  }, [location.state]);

  useEffect(() => {
    if (activeTab === 'trips') {
      fetchVehicleTrips();
    }
  }, [activeTab, tripsPageInfo.currentPage]);

  const fetchVehicle = async () => {
    const { data } = await get(`/api/vehicles/${id}`);
    if (data) setVehicle(data);
  };

  const fetchVehicleTrips = async () => {
    setLoadingTrips(true);
	
    const endpoint = `/api/vehicles/${id}/trips?pageNumber=${tripsPageInfo.currentPage}&itemsPerPage=${tripsPageInfo.size}`;
	
    const { data } = await get(endpoint);
	
    if (data) {
      setTrips(data.content || []);
      setTripsPageInfo({
        totalElements: data.totalElements || 0,
        totalPages: data.totalPages || 0,
        currentPage: data.number || 0,
        size: data.size || 10
      });
    }
	
    setLoadingTrips(false);
  };

  const tripColumns = [
    { header: 'ID', accessor: 'id' },
    { header: 'Label', accessor: (row) => row.label || `Trip #${row.id}` },
    { header: 'Type', accessor: (row) => row.type || 'Standard' },
    { 
      header: 'Driver', 
      accessor: (row) => row.driver?.fullName || 'Unknown'
    },
    { 
      header: 'Started', 
      accessor: (row) => row.startedAt ? new Date(row.startedAt).toLocaleString() : '-'
    },
    { 
      header: 'Ended', 
      accessor: (row) => row.endedAt ? new Date(row.endedAt).toLocaleString() : '-'
    },
    { 
      header: 'Status', 
      accessor: (row) => (
        <span className={`px-2 py-1 rounded-full text-xs ${
          row.endedAt ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' : 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 animate-pulse'
        }`}>
          {row.endedAt ? 'Completed' : 'In Progress'}
        </span>
      )
    },
    { 
      header: 'Devices', 
      accessor: (row) => row.devices?.length || 0
    },    {
      header: 'Actions',
      accessor: (row) => (
        <button
          onClick={() => navigate(`/trips/${row.id}`)}
          className="text-indigo-600 hover:text-indigo-900 dark:text-indigo-400 dark:hover:text-indigo-300 text-sm font-medium"
        >
          View Trip
        </button>
      )
    }
  ];

  if (loading) return <LoadingSpinner />;
  if (!vehicle) return <div className="p-6 text-gray-900 dark:text-white">Vehicle not found</div>;

  return (
    <div className="p-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center">
          <button
            onClick={() => navigate('/vehicles/filter')}
            className="mr-4 text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200"
          >
            <ArrowLeftIcon className="h-6 w-6" />
          </button>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Vehicle Details</h1>
        </div>
        <div className="flex space-x-2">
          {isAdmin && (
            <button
              onClick={() => navigate(`/vehicles/edit/${id}`)}
              className="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 flex items-center space-x-2"
            >
              <PencilIcon className="h-5 w-5" />
              <span>Edit Vehicle</span>
            </button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 dark:border-gray-700 mb-6">
        <nav className="flex space-x-8">
          <button
            onClick={() => setActiveTab('info')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'info'
                ? 'border-indigo-500 text-indigo-600 dark:text-indigo-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
            }`}
          >
            Vehicle Information
          </button>
          <button
            onClick={() => setActiveTab('trips')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'trips'
                ? 'border-indigo-500 text-indigo-600 dark:text-indigo-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
            }`}
          >
            Trip History
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'info' ? (
        /* Vehicle Information Tab */
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="text-sm text-gray-500 dark:text-gray-400">ID</label>
              <p className="text-lg font-medium text-gray-900 dark:text-white">{vehicle.id}</p>
            </div>
            <div>
              <label className="text-sm text-gray-500 dark:text-gray-400">Brand</label>
              <p className="text-lg font-medium text-gray-900 dark:text-white">{vehicle.brand}</p>
            </div>
            <div>
              <label className="text-sm text-gray-500 dark:text-gray-400">Color</label>
              <p className="text-lg font-medium text-gray-900 dark:text-white">{vehicle.color}</p>
            </div>
            <div>
              <label className="text-sm text-gray-500 dark:text-gray-400">Plates</label>
              <p className="text-lg font-medium text-gray-900 dark:text-white">{vehicle.plates}</p>
            </div>
            <div>
																						 
																										  
				  
				 
              <label className="text-sm text-gray-500 dark:text-gray-400">Started At</label>
              <p className="text-lg font-medium text-gray-900 dark:text-white">
                {vehicle.startedAt ? new Date(vehicle.startedAt).toLocaleString() : '-'}
              </p>
            </div>
            <div>
              <label className="text-sm text-gray-500 dark:text-gray-400">Ended At</label>
              <p className="text-lg font-medium text-gray-900 dark:text-white">
                {vehicle.endedAt ? new Date(vehicle.endedAt).toLocaleString() : '-'}
              </p>
            </div>
            <div>
              <label className="text-sm text-gray-500 dark:text-gray-400">Status</label>
              <p className="text-lg font-medium">
                <span className={`px-2 py-1 rounded-full text-xs ${
                  vehicle.isActive 
                    ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' 
                    : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                }`}>
                  {vehicle.isActive ? 'Active' : 'Inactive'}
                </span>
              </p>
            </div>
            {vehicle.other && (
              <div className="md:col-span-2">
                <label className="text-sm text-gray-500 dark:text-gray-400">Other Information</label>
                <p className="text-lg font-medium text-gray-900 dark:text-white">{vehicle.other}</p>
              </div>
            )}
          </div>
        </div>
      ) : (
        /* Trips History Tab */
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-6">Trip History</h2>

          {loadingTrips ? (
            <LoadingSpinner />
          ) : trips.length > 0 ? (
            <>
              <DataTable 
                columns={tripColumns} 
                data={trips}
                pageInfo={tripsPageInfo}
                onPageChange={(page) => setTripsPageInfo({...tripsPageInfo, currentPage: page})}
              />
              
              {/* Trip Summary Stats */}
              <div className="mt-4 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Trip Summary</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <span className="text-xs text-gray-500 dark:text-gray-400">Total Trips</span>
                    <p className="text-lg font-semibold text-gray-900 dark:text-white">{trips.length}</p>
                  </div>
                  <div>
                    <span className="text-xs text-gray-500 dark:text-gray-400">Completed</span>
                    <p className="text-lg font-semibold text-green-600 dark:text-green-400">
                      {trips.filter(t => t.endedAt).length}
                    </p>
                  </div>
                  <div>
                    <span className="text-xs text-gray-500 dark:text-gray-400">In Progress</span>
                    <p className="text-lg font-semibold text-blue-600 dark:text-blue-400">
                      {trips.filter(t => !t.endedAt).length}
                    </p>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className="text-center py-12">
              <CalendarIcon className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">No trips</h3>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                This vehicle has no trips yet.
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default VehicleDetails;